import { useMemo, useState } from "react";
import { CheckCircle2, Search } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Remove acentos e converte para lowercase para busca precisa
 */
const normalizeString = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

interface SearchableSelectionListProps<T> {
  items: T[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  getId: (item: T) => number;
  getLabel: (item: T) => string;
  emptyMessage: string;
  searchPlaceholder: string;
  disabled?: boolean;
}

/**
 * Lista de seleção com busca e ordem estável.
 * A ordenação é memoizada apenas em `items`, então marcar/desmarcar nunca
 * reordena os itens (sem salto de posição). O toggle é centralizado no clique
 * da linha; o checkbox é apenas visual (pointer-events-none) para evitar
 * duplo-disparo.
 *
 * Reutilizado pela criação de usuário (páginas do wizard) e pela edição
 * (PessoaDialogEdit) para seleção de cargos e squads.
 */
export function SearchableSelectionList<T>({
  items,
  selectedIds,
  onToggle,
  getId,
  getLabel,
  emptyMessage,
  searchPlaceholder,
  disabled,
}: SearchableSelectionListProps<T>) {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => getLabel(a).localeCompare(getLabel(b))),
    [items, getLabel],
  );

  const filteredItems = useMemo(() => {
    const term = normalizeString(search.trim());
    if (!term) return sortedItems;
    return sortedItems.filter((item) => normalizeString(getLabel(item)).includes(term));
  }, [sortedItems, search, getLabel]);

  return (
    <div className="flex h-[300px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="shrink-0 border-b border-border bg-muted/30 p-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            disabled={disabled || items.length === 0}
            className="h-8 bg-background pl-8 text-sm"
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center bg-muted/30 p-4 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-4 text-center text-sm text-muted-foreground">
          Nenhum resultado para “{search}”.
        </div>
      ) : (
        <div className="custom-scrollbar flex-1 divide-y divide-border overflow-y-auto p-1">
          {filteredItems.map((item) => {
            const id = getId(item);
            const isSelected = selectedSet.has(id);
            return (
              <div
                key={id}
                onClick={() => !disabled && onToggle(id)}
                className={cn(
                  "mb-1 flex cursor-pointer items-center justify-between rounded-lg border border-transparent p-3 transition-all duration-200 last:mb-0",
                  isSelected
                    ? "border-primary/20 bg-primary/10 text-foreground"
                    : "hover:bg-accent hover:text-accent-foreground",
                  disabled && "pointer-events-none cursor-not-allowed opacity-50",
                )}
              >
                <div className="flex min-w-0 items-center space-x-3">
                  <Checkbox
                    checked={isSelected}
                    tabIndex={-1}
                    aria-hidden
                    className={cn(
                      "pointer-events-none border-primary/50 transition-all",
                      isSelected &&
                        "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
                    )}
                  />
                  <Label className="cursor-pointer truncate text-sm font-medium">
                    {getLabel(item)}
                  </Label>
                </div>
                {isSelected && (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary animate-in zoom-in" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
